import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { toast } from "sonner";

export default function Register() {
    const { register } = useAuth();
    const { t } = useT();
    const navigate = useNavigate();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 6) {
            toast.error(t("toast.passwordMin"));
            return;
        }
        setSubmitting(true);
        try {
            const user = await register(name, email, password);
            toast.success(t("toast.welcomeNew", user.name?.split(" ")[0] || "guest"));
            navigate("/dashboard", { replace: true });
        } catch (err) {
            toast.error(err.response?.data?.detail || t("toast.registerFailed"));
        } finally {
            setSubmitting(false);
        }
    };

    const googleSignIn = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/dashboard";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div data-testid="register-page" className="bg-bone min-h-screen">
            <Navbar />
            <section className="pt-32 pb-24 px-6 lg:px-12">
                <div className="max-w-md mx-auto">
                    <div className="eyebrow mb-4 text-center">{t("auth.beginStay")}</div>
                    <h1 className="font-chiller text-5xl text-center leading-tight mb-12">{t("auth.createTitle")}</h1>

                    <form onSubmit={submit} className="space-y-6" data-testid="register-form">
                        <Field label={t("auth.yourName")} value={name} setValue={setName} testid="register-name" />
                        <Field label={t("auth.email")} type="email" value={email} setValue={setEmail} testid="register-email" />
                        <Field label={t("auth.passwordMin")} type="password" value={password} setValue={setPassword} testid="register-password" />
                        <button
                            type="submit"
                            data-testid="register-submit"
                            disabled={submitting}
                            className="w-full px-6 py-4 bg-forest text-bone hover:bg-forestDark text-xs uppercase tracking-widest2 font-semibold disabled:opacity-50"
                        >
                            {submitting ? t("auth.creating") : t("auth.createAccount")}
                        </button>
                    </form>

                    <div className="my-8 flex items-center gap-4 text-xs uppercase tracking-widest2 text-espresso/60">
                        <div className="hairline flex-1" /> {t("auth.or")} <div className="hairline flex-1" />
                    </div>

                    <button
                        onClick={googleSignIn}
                        data-testid="register-google"
                        className="w-full px-6 py-4 border border-espresso/30 hover:bg-bone2 text-xs uppercase tracking-widest2 font-semibold inline-flex items-center justify-center gap-3"
                    >
                        <span className="w-4 h-4 rounded-full bg-gradient-to-br from-terracotta to-forest" /> {t("auth.continueGoogle")}
                    </button>

                    <p className="mt-10 text-center text-sm text-muted2">
                        {t("auth.alreadyGuest")}{" "}
                        <Link to="/login" className="text-forest hover:text-terracotta underline" data-testid="link-to-login">
                            {t("auth.signIn")}
                        </Link>
                    </p>
                </div>
            </section>
            <Footer />
        </div>
    );
}

function Field({ label, value, setValue, type = "text", testid }) {
    return (
        <div>
            <label className="eyebrow block mb-2">{label}</label>
            <input
                data-testid={testid}
                type={type}
                required
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="w-full bg-transparent border-b border-espresso/30 focus:border-forest outline-none py-3"
            />
        </div>
    );
}
